import { useEffect, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { Package } from "lucide-react";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { BillingFormSection } from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "../../BonificoCheckout/bonificoBillingValidation.ts";
import { BusinessType } from "../../MoneyTransfer/BusinessType.tsx";
import { useCreateBonificoOrder } from "../../ProductService/BonificoOrder/useCreateBonificoOrder.ts";
import { toBonificoBillingFormFields } from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import {
  BonificoOrderDTO,
  ExternalOrderPaymentType,
  ExternalSettlementGateway,
  externalSettlementGatewayLabel,
  getBonificoOrderErrorMessage,
  VatExemption,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { isTestItem } from "../../ProductService/isTestItem.ts";
import { PurchasableItemPickerModal } from "./PurchasableItemPickerModal.tsx";
import { Article7TerSwitch } from "./Article7TerSwitch.tsx";
import { isArticle7Ter } from "../../ProductService/BonificoOrder/article7Ter.ts";
import { InvoiceItemDescriptionInput } from "./InvoiceItemDescriptionInput.tsx";
import {
  chosenInvoiceItemDescription,
  invoiceItemDescriptionPlaceholder,
  standardInvoiceItemDescriptionOfItem,
} from "../../ProductService/BonificoOrder/invoiceItemDescription.ts";
import {
  eurosToCents,
  ExternalSettlementFormValues,
  originalPaymentTypeOf,
  remainingAmountInEuros,
  sellsCatalogProducts,
  toExternalSettlementPayload,
} from "./externalSettlementValues.ts";

const GATEWAY_OPTIONS = [
  { value: ExternalSettlementGateway.SamCart, label: "SamCart" },
  { value: ExternalSettlementGateway.Stripe, label: "Stripe" },
  { value: ExternalSettlementGateway.HeyLight, label: "HeyLight" },
  { value: ExternalSettlementGateway.Other, label: "Altro" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: ExternalOrderPaymentType.Installments, label: "A rate" },
  { value: ExternalOrderPaymentType.OneTime, label: "Soluzione unica" },
];

const CUSTOMER_FIELD_LABELS = {
  email: "Email",
  firstName: "Nome",
  lastName: "Cognome",
} as const;

const eurosSchema = yup.number().typeError("Inserisci un importo valido");

const schema = yup
  .object({
    gateway: yup
      .string()
      .oneOf(Object.values(ExternalSettlementGateway))
      .required(),
    otherGatewayName: yup.string().when("gateway", {
      is: ExternalSettlementGateway.Other,
      then: (s) => s.trim().required("Indica il nome del gateway"),
      otherwise: (s) => s.notRequired(),
    }),
    externalOrderId: yup
      .string()
      .trim()
      .required("L'ID dell'ordine è obbligatorio"),
    externalProductId: yup.string().trim().optional(),
    normasteachingProductId: yup
      .string()
      .required("Scegli il prodotto del catalogo"),
    customer: yup.object({
      email: yup
        .string()
        .trim()
        .email("Email non valida")
        .required("Obbligatorio"),
      firstName: yup.string().trim().required("Obbligatorio"),
      lastName: yup.string().trim().required("Obbligatorio"),
    }),
    receiptDate: yup
      .string()
      .required("La data della contabile è obbligatoria"),
    originalPaymentType: yup
      .string()
      .oneOf(Object.values(ExternalOrderPaymentType))
      .required(),
    numberOfInstallments: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Inserisci un numero di rate valido")
      .integer("Inserisci un numero intero")
      .min(2, "Il piano prevede almeno 2 rate")
      .optional(),
    totalAmountInEuros: eurosSchema
      .moreThan(0, "L'importo deve essere maggiore di zero")
      .required("Inserisci un importo valido"),
    amountAlreadyPaidInEuros: eurosSchema
      .min(0, "L'importo non può essere negativo")
      .required("Inserisci un importo valido")
      .test(
        "less-than-total",
        "Deve essere inferiore al totale dell'ordine",
        function (amountAlreadyPaid) {
          const total = this.parent.totalAmountInEuros;
          if (!Number.isFinite(total) || amountAlreadyPaid === undefined)
            return true;
          return eurosToCents(amountAlreadyPaid) < eurosToCents(total);
        },
      ),
    settlementAmountInEuros: eurosSchema
      .moreThan(0, "L'importo deve essere maggiore di zero")
      .required("Inserisci un importo valido"),
    ...bonificoBillingSchemaFields,
  })
  .test("has-at-least-sdi-or-pec", "", hasSdiOrPec);

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {children}
      </label>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {hint && <div className="text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function customerFieldsOf(
  draft?: BonificoOrderDTO,
): ExternalSettlementFormValues["customer"] {
  return {
    email: draft?.customer.email ?? "",
    firstName: draft?.customer.firstName ?? "",
    lastName: draft?.customer.lastName ?? "",
  };
}

export function ExternalSettlementForm({
  draft,
  onSaved,
  onCancel,
}: {
  draft?: BonificoOrderDTO;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const {
    createExternalSettlement,
    confirmDraftAsExternalSettlement,
    loading,
  } = useCreateBonificoOrder();
  const { products } = useAllNormasTeachingProducts();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPickingProduct, setIsPickingProduct] = useState(false);
  const [isSettlementEdited, setIsSettlementEdited] = useState(false);
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(draft?.vatExemption),
  );
  const [invoiceItemDescription, setInvoiceItemDescription] = useState(
    draft?.invoiceItemDescription ?? "",
  );
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<ExternalSettlementFormValues>({
    resolver: yupResolver(
      schema,
    ) as unknown as Resolver<ExternalSettlementFormValues>,
    defaultValues: {
      gateway: ExternalSettlementGateway.SamCart,
      otherGatewayName: "",
      externalOrderId: "",
      externalProductId: "",
      normasteachingProductId: "",
      customer: customerFieldsOf(draft),
      receiptDate: "",
      originalPaymentType: ExternalOrderPaymentType.Installments,
      ...toBonificoBillingFormFields(draft),
      invoiceRequired: true,
      businessType:
        draft?.businessInformation?.businessType ?? BusinessType.Person,
    },
  });

  const catalogProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          !isTestItem(product.productName, product.variantProductName),
      ),
    [products],
  );
  const gateway = watch("gateway");
  const gatewayLabel = externalSettlementGatewayLabel({
    gateway,
    otherGatewayName: watch("otherGatewayName"),
  });
  const isCatalogGateway = sellsCatalogProducts(gateway);
  const isInstallmentPlan =
    originalPaymentTypeOf(gateway, watch("originalPaymentType")) ===
    ExternalOrderPaymentType.Installments;
  const selectedProductId = watch("normasteachingProductId");
  const selectedProduct = products.find(
    (product) => product._id === selectedProductId,
  );
  const remainingInEuros = remainingAmountInEuros(
    watch("totalAmountInEuros"),
    watch("amountAlreadyPaidInEuros"),
  );
  const settlementAmountInEuros = watch("settlementAmountInEuros");
  const isSettlementDifferentFromRemaining =
    isSettlementEdited &&
    remainingInEuros !== undefined &&
    Number(settlementAmountInEuros) !== remainingInEuros;

  useEffect(() => {
    if (isSettlementEdited || remainingInEuros === undefined) return;
    setValue("settlementAmountInEuros", remainingInEuros, {
      shouldValidate: isSubmitted,
    });
  }, [isSettlementEdited, remainingInEuros, isSubmitted, setValue]);

  const onSubmit = async (values: ExternalSettlementFormValues) => {
    setSubmitError(null);
    try {
      const payload = {
        ...toExternalSettlementPayload(values),
        ...(isArticle7TerApplied && {
          vatExemption: VatExemption.Article7Ter,
        }),
        invoiceItemDescription: chosenInvoiceItemDescription(
          invoiceItemDescription,
        ),
      };
      if (draft) await confirmDraftAsExternalSettlement(draft.orderId, payload);
      else await createExternalSettlement(payload);
      onSaved();
    } catch (e) {
      setSubmitError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante la creazione del saldo. Riprova.",
        ),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Ordine originale</h2>
        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-medium text-gray-700 mb-1">
            Gateway di pagamento *
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {GATEWAY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
              >
                <input
                  type="radio"
                  value={option.value}
                  {...register("gateway")}
                  className="accent-primary"
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>
        {isCatalogGateway ? (
          <p className="text-xs text-gray-500">
            Gli ordini {gatewayLabel} si integrano solo se pagati in soluzione
            unica.
            {gateway === ExternalSettlementGateway.Stripe &&
              " Il saldo delle rate di un ordine Stripe si registra dalla tabella degli ordini Stripe."}
          </p>
        ) : (
          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-gray-700 mb-1">
              Pagamento dell'ordine originale *
            </legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...register("originalPaymentType")}
                    className="accent-primary"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gateway === ExternalSettlementGateway.Other && (
            <Field
              label="Nome del gateway *"
              error={errors.otherGatewayName?.message}
            >
              <TextInput {...register("otherGatewayName")} />
            </Field>
          )}
          <Field
            label={`ID ordine ${gatewayLabel} *`}
            error={errors.externalOrderId?.message}
          >
            <TextInput {...register("externalOrderId")} />
          </Field>
          {isInstallmentPlan && (
            <Field
              label="Numero di rate del piano (facoltativo)"
              error={errors.numberOfInstallments?.message}
            >
              <TextInput
                type="number"
                step="1"
                min="2"
                {...register("numberOfInstallments")}
              />
            </Field>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Prodotto</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="Prodotto del catalogo *"
            error={errors.normasteachingProductId?.message}
            hint="Usato per la fattura e per BigQuery: scegli quello che corrisponde al prodotto acquistato."
          >
            <button
              type="button"
              onClick={() => setIsPickingProduct(true)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <Package className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="min-w-0 break-words">
                {selectedProduct
                  ? `${selectedProduct.productName} · ${selectedProduct.variantProductName}`
                  : "Scegli il prodotto"}
              </span>
            </button>
          </Field>
          {!isCatalogGateway && (
            <Field
              label={`ID prodotto ${gatewayLabel} (facoltativo)`}
              error={errors.externalProductId?.message}
              hint="Se lo indichi, su BigQuery la transazione avrà l'ID prodotto dell'ordine originale al posto di quello del catalogo."
            >
              <TextInput {...register("externalProductId")} />
            </Field>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Importi e contabile</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field
            label="Totale ordine (€) *"
            error={errors.totalAmountInEuros?.message}
          >
            <TextInput
              type="number"
              step="0.01"
              min="0"
              {...register("totalAmountInEuros")}
            />
          </Field>
          <Field
            label={`Già pagato su ${gatewayLabel} (€) *`}
            error={errors.amountAlreadyPaidInEuros?.message}
          >
            <TextInput
              type="number"
              step="0.01"
              min="0"
              {...register("amountAlreadyPaidInEuros")}
            />
          </Field>
          <Field
            label="Saldo con bonifico (€) *"
            error={errors.settlementAmountInEuros?.message}
            hint={
              isSettlementDifferentFromRemaining ? (
                <span>
                  Il residuo è €{remainingInEuros.toFixed(2)}.{" "}
                  <button
                    type="button"
                    onClick={() => setIsSettlementEdited(false)}
                    className="text-blue-600 hover:underline"
                  >
                    Usa il residuo
                  </button>
                </span>
              ) : (
                "Precompilato con il residuo, puoi modificarlo."
              )
            }
          >
            <TextInput
              type="number"
              step="0.01"
              min="0"
              {...register("settlementAmountInEuros", {
                onChange: () => setIsSettlementEdited(true),
              })}
            />
          </Field>
          <Field
            label="Data della contabile *"
            error={errors.receiptDate?.message}
          >
            <input
              type="date"
              max={dayjs().format("YYYY-MM-DD")}
              {...register("receiptDate")}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700"
            />
          </Field>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Cliente</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(["email", "firstName", "lastName"] as const).map((key) => (
            <Field
              key={key}
              label={`${CUSTOMER_FIELD_LABELS[key]} *`}
              error={errors.customer?.[key]?.message}
            >
              <TextInput {...register(`customer.${key}`)} />
            </Field>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
        <h2 className="font-semibold text-lg">Dati di fatturazione</h2>
        <BillingFormSection
          control={control}
          register={register}
          errors={errors}
          invoiceRequired={true}
          businessType={watch("businessType")}
          taxCode={watch("taxCode")}
          isBusinessInformationRequired={true}
          isInvoiceMandatory={true}
          trigger={trigger}
          askBillingCountry
          onArticle7TerSuggestion={setIsArticle7TerApplied}
        />
        <Article7TerSwitch
          checked={isArticle7TerApplied}
          onChange={setIsArticle7TerApplied}
        />
        <InvoiceItemDescriptionInput
          value={invoiceItemDescription}
          placeholder={invoiceItemDescriptionPlaceholder(
            standardInvoiceItemDescriptionOfItem(selectedProduct),
          )}
          onChange={setInvoiceItemDescription}
        />
      </div>

      <p className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
        Il saldo resta in attesa di pagamento. Quando lo segni come pagato
        vengono emesse la fattura (Bonifici Italia) e la transazione su BigQuery
        alla data di valuta,{" "}
        {isInstallmentPlan
          ? `come rata dell'ordine ${gatewayLabel}. Su ${gatewayLabel} non viene toccato nulla: la subscription va chiusa a mano.`
          : `come integrazione dell'ordine ${gatewayLabel}: l'importo si aggiunge al venduto dell'ordine. Su ${gatewayLabel} non viene toccato nulla.`}
      </p>

      {submitError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label={draft ? "Conferma come saldo" : "Crea saldo"}
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          disabled={loading}
        />
      </div>

      <PurchasableItemPickerModal
        isOpen={isPickingProduct}
        onClose={() => setIsPickingProduct(false)}
        products={catalogProducts}
        bundles={[]}
        title="Scegli il prodotto acquistato"
        selected={
          selectedProductId
            ? { type: "product", id: selectedProductId }
            : undefined
        }
        onPick={(item) =>
          setValue("normasteachingProductId", item.id, {
            shouldValidate: isSubmitted,
          })
        }
      />
    </form>
  );
}
